import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { NavigationTextButton } from "components";
import { Grid } from "@material-ui/core";
import { dealAnalysisGetDealMetrics, getRefiCalcResults, cashoutReCalcForDeal } from "pages/dealAnalysis/dealAnalysis.store";
import PageWrapper from "components/Global/PageOrganization/PageWrapper";
import { LenderLoanTypeList } from "constants/loanTypes";
import { ROUTE_DEAL_SCREENER } from "constants/routes";

import { LBFooter } from "../../../components/LBFooter";

import {
  RefiAnalysisType,
  SamplePropertyMetrics,
  RefiAnalysisTypeOptions
} from "./FilterSection/constants";

import {
  MetricCell,
  FilterSection,
  ProceedsCard
} from "./";
import "./styles.css";

const RefiAnalysisUnsupervised = ({ match, history, location, deal, cashoutReCalcForDeal, dealAnalysisGetDealMetrics, getRefiCalcResults, refiCalcResults }) => {
  const { dealId } = match.params;
  const [dealName, setDealName] = useState("");
  const [backRoute, setBackRoute] = useState({ name: "Deal Screener", route: ROUTE_DEAL_SCREENER });
  const [refiAnalysisType, setRefiAnalysisType] = useState(RefiAnalysisType.CASHOUT);
  const [selectedMaxProceedsFilter, setSelectedMaxProceedsFilter] = useState(0);
  const [selectedNoiAdjustment, setSelectedNoiAdjustment] = useState(0);
  const [refiProceeds, setRefiProceeds] = useState([]);
  const { refiResults = {}, favoritedTerms = [] } = refiCalcResults;

  useEffect(() => {
    dealAnalysisGetDealMetrics({ dealId });
    getRefiCalcResults({ dealId });
  }, []);

  useEffect(() => {
    setDealName(deal?.name);
  }, [deal]);

  useEffect(() => {
    const prevLocation = location?.state?.from ?? ROUTE_DEAL_SCREENER;
    prevLocation === ROUTE_DEAL_SCREENER ?
      setBackRoute({ name: "Deal Screener", route: prevLocation }) :
      setBackRoute({ name: "Metrics & Analysis", route: prevLocation });
  }, [location]);

  useEffect(() => {
    const formattedProceeds = Object.entries(refiResults).map(([term, loanTypeGroup]) => {
      const proceeds = Object.entries(loanTypeGroup).map(([loanType, refiResults]) => {
        const filterOption = RefiAnalysisTypeOptions.filter(opt => refiAnalysisType === opt.name)[0];
        const ltvFilteredValues = refiResults.filter(constraint => (selectedMaxProceedsFilter ? (constraint.ltvMax) <= selectedMaxProceedsFilter : true));
        const noiFiltered = ltvFilteredValues.filter(({ noi }) => noi === selectedNoiAdjustment);
        const maxValue = Math.max.apply(Math, noiFiltered.map(o => o[filterOption.resultType]));
        const maxObject = noiFiltered.find(refi => refi[filterOption.resultType] === maxValue);
        const loanTypeName = LenderLoanTypeList.filter(val => loanType == val.value)[0].label;

        return { loanTypeName, ...maxObject };
      });

      return { term: parseInt(term), proceeds };
    });

    const favProceeds = formattedProceeds.filter(proceed => favoritedTerms.includes(proceed.term)).sort((a, b) => a.term - b.term);
    const unfavProceeds = formattedProceeds.filter(proceed => !favoritedTerms.includes(proceed.term)).sort((a, b) => a.term - b.term);
    setRefiProceeds([...favProceeds, ...unfavProceeds]);
  }, [refiCalcResults, selectedNoiAdjustment, selectedMaxProceedsFilter]);

  const handleRefreshClicked = () => {
    setRefiAnalysisType(RefiAnalysisType.CASHOUT);
    setSelectedMaxProceedsFilter(0);
    setSelectedNoiAdjustment(0);
    cashoutReCalcForDeal({ dealId });
  };

  return (
    <div className="refi-proceeds-analysis-root">
      <NavigationTextButton
        navigationType="back"
        buttonText={backRoute.name ?? "Back"}
        onClick={() => history.push(backRoute.route, { from: history.location.pathname })}
      />
      <PageWrapper>
        <Grid container justify="center" direction="column" style={{ padding: "0 8em" }}>
          <Grid container item xs={12} justify="space-between">
            <div className="refi-proceeds-analysis-title">{`${refiAnalysisType} Analysis`}</div>
            <div className="refi-proceeds-analysis-address">{dealName}</div>
          </Grid>
          <Grid item>
            <div className="refi-proceeds-analysis-metric-cells-wrapper">
              {SamplePropertyMetrics.map(((metric, idx) => {
                let value = refiCalcResults?.metrics?.[metric?.metricId];
                if (metric.adjust) {
                  value = metric.adjust(value, refiCalcResults?.metrics, selectedNoiAdjustment);
                }

                return (<MetricCell key={idx} {...metric} value={value} />);
              }))}
            </div>
          </Grid>
          <Grid item>
            <FilterSection
              refiAnalysisType={refiAnalysisType}
              selectedNoiAdjustment={selectedNoiAdjustment}
              selectedMaxProceedsFilter={selectedMaxProceedsFilter}
              setRefiAnalysisType={val => setRefiAnalysisType(val)}
              setSelectedNoiAdjustment={val => setSelectedNoiAdjustment(val)}
              setSelectedMaxProceedsFilter={val => setSelectedMaxProceedsFilter(val)}
              handleRefreshClicked={() => handleRefreshClicked()}
            />
          </Grid>
          <Grid container item className="refi-proceeds-analysis-proceeds-cards-wrapper" justify="space-between">
            {refiProceeds.map(({ term, proceeds }) => <ProceedsCard refiAnalysisType={refiAnalysisType} term={term} dealId={dealId} favoritedTerms={favoritedTerms} lenderProceeds={proceeds} />)}
          </Grid>
          <div className="refi-analysis-unsupervised-footer-container">
            <LBFooter />
          </div>
        </Grid>
      </PageWrapper>
    </div>
  );
};

const mapStateToProps = ({ dealAnalysis }) => ({ ...dealAnalysis });

export default compose(connect
(
  mapStateToProps,
  {
    dealAnalysisGetDealMetrics,
    getRefiCalcResults,
    cashoutReCalcForDeal
  }
))(RefiAnalysisUnsupervised);
